import { OnboardingProgress } from "@/components/OnboardingProgress";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fileUrl, errMsg, CATEGORIES } from "@/lib/api";
import YourStatsCard from "@/components/YourStatsCard";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Store, Upload, Trash2, FileText, Plus, X, Inbox, Clock, CheckCircle2, XCircle,
  CreditCard, Mail, Pencil, Loader2,
} from "lucide-react";
import InviteTrialBadge from "@/components/InviteTrialBadge";

const EMPTY_BRANCH = { name: "", address: "", city: "", province: "", phone: "", email: "" };
const EMPTY_PROFILE = {
  business_name: "", tagline: "", description: "", categories: [],
  location: "", phone: "", contact_email: "", website: "", social_media: "", services: [],
  logo_path: "", image_paths: [], flyer_paths: [],
  has_multiple_branches: false, branches: [],
};

// This single component renders TWO distinct screens off the same route:
//
// 1. Sign-up form (vendor_status !== "approved" OR profile not yet completed):
//    one scrolling page with every business detail field + logo / photo / PDF
//    uploads + a single "Submit for Approval" button + blocking confirmation
//    modal. No tabs.
//
// 2. Dashboard (approved vendors): unchanged tabbed layout from before — the
//    status banner, business summary card, Photos & Flyers tab, and Enquiries
//    inbox tab. Nothing in this branch should be modified.
export default function VendorPortal() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(EMPTY_PROFILE);
  const [status, setStatus] = useState("pending");
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState("");
  const [tab, setTab] = useState("media");
  const logoInput = useRef(null);
  const imageInput = useRef(null);
  const flyerInput = useRef(null);

  const load = () => {
    api.get("/vendor/profile").then((r) => {
      setProfile({ ...EMPTY_PROFILE, ...r.data.profile });
      setStatus(r.data.vendor_status);
    }).catch(() => {});
    api.get("/vendor/requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const refreshUploads = async () => {
    try {
      const { data } = await api.get("/vendor/profile");
      const p = data.profile || {};
      setProfile((prev) => ({
        ...prev,
        logo_path: p.logo_path || "",
        image_paths: p.image_paths || [],
        flyer_paths: p.flyer_paths || [],
      }));
    } catch { /* ignore — user can retry */ }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("File too large (max 10MB)");
    setUploading(kind);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/vendor/upload?kind=${kind}`, fd);
      toast.success("Uploaded!");
      await refreshUploads();
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploading("");
    }
  };

  const removeFile = async (path, kind) => {
    try {
      await api.delete(`/vendor/file?path=${encodeURIComponent(path)}&kind=${kind}`);
      toast.success("Removed");
      await refreshUploads();
    } catch {
      toast.error("Could not remove file");
    }
  };

  const toggleCategory = (c) => {
    const cats = profile.categories.includes(c)
      ? profile.categories.filter((x) => x !== c)
      : [...profile.categories, c];
    setProfile({ ...profile, categories: cats });
  };

  const addService = () =>
    setProfile({ ...profile, services: [...profile.services, { name: "", description: "", price: "" }] });
  const setServiceField = (i, key, value) => {
    const arr = [...profile.services];
    arr[i] = { ...arr[i], [key]: value };
    setProfile({ ...profile, services: arr });
  };
  const removeService = (i) =>
    setProfile({ ...profile, services: profile.services.filter((_, j) => j !== i) });

  const save = async () => {
    if (!profile.business_name.trim()) {
      toast.error("Business name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        business_name: profile.business_name, tagline: profile.tagline,
        description: profile.description, categories: profile.categories,
        location: profile.location, phone: profile.phone,
        contact_email: profile.contact_email, website: profile.website,
        social_media: profile.social_media, services: profile.services,
        has_multiple_branches: !!profile.has_multiple_branches,
        branches: profile.has_multiple_branches ? profile.branches : [],
      };
      const { data } = await api.put("/vendor/profile", payload);
      setProfile((prev) => ({ ...prev, ...data }));
      const fresh = await refresh();
      if (fresh?.vendor_status === "approved" && fresh?.subscription_status === "active") {
        toast.success("Profile saved!");
        setSaving(false);
        return;
      }
      // First submission (or still-pending vendor): show the blocking modal.
      setSubmitted(true);
    } catch (e) {
      toast.error(errMsg(e, "Could not save profile"));
    } finally {
      setSaving(false);
    }
  };

  // The modal only closes when the user clicks the built-in X button
  // (DialogClose). onPointerDownOutside + onEscapeKeyDown are
  // preventDefaulted on <DialogContent>, so clicking outside or pressing
  // Escape does nothing. Closing routes the user to login (and signs out).
  const closeModal = async () => {
    setSubmitted(false);
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    await logout();
    navigate("/login", { replace: true });
  };

  const statusBanner = {
    pending: { icon: Clock, cls: "bg-amber-50 border-amber-200 text-amber-800", text: "Your account is awaiting admin approval. Complete your profile meanwhile — it goes live once approved." },
    approved: { icon: CheckCircle2, cls: "bg-sage-light border-sage/30 text-sage-dark", text: "Your account is approved and visible in the directory." },
    rejected: { icon: XCircle, cls: "bg-terracotta-light border-terracotta/30 text-terracotta-dark", text: "Your application was not approved. Contact the community admin for details." },
  }[status] || {};

  // ───────────────────────────────────────────────────────────────────────
  // SIGN-UP: single-page form (vendor not approved yet).
  // ───────────────────────────────────────────────────────────────────────
  if (status !== "approved") {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10" data-testid="vendor-portal">
        <OnboardingProgress />
        <div className="flex items-center gap-3 mb-6 fade-in">
          <span className="w-11 h-11 rounded-full bg-sage flex items-center justify-center">
            <Store className="w-5 h-5 text-white" />
          </span>
          <div>
            <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Vendor sign-up</p>
            <h1 className="text-2xl sm:text-3xl font-black text-forest mt-0.5">
              Tell us about your business
            </h1>
            <p className="text-sm text-forest/60">
              Fill in your details below and submit for admin approval. We'll email you once you're approved so you can complete payment and go live.
            </p>
          </div>
        </div>

        {statusBanner.text && (
          <div className={`flex items-start gap-3 border rounded-2xl p-4 mb-6 text-sm ${statusBanner.cls}`} data-testid="vendor-status-banner">
            <statusBanner.icon className="w-5 h-5 shrink-0 mt-0.5" />
            <p>{statusBanner.text}</p>
          </div>
        )}

        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6" data-testid="vendor-signup-form">
          {/* Logo */}
          <section>
            <Label className="text-forest/80 font-bold">Business logo</Label>
            <div className="mt-2 flex items-center gap-4">
              {profile.logo_path ? (
                <div className="relative w-24 h-24 rounded-2xl overflow-hidden border border-sage/30 shrink-0">
                  <img src={fileUrl(profile.logo_path)} alt="Business logo" className="w-full h-full object-cover" data-testid="vs-logo-preview" />
                  <button type="button" onClick={() => removeFile(profile.logo_path, "logo")}
                    className="absolute top-1 right-1 bg-white/90 hover:bg-white p-1 rounded-full text-terracotta-dark"
                    aria-label="Remove logo" data-testid="vs-logo-remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-softstone/60 border border-dashed border-sage/40 flex items-center justify-center text-forest/40 shrink-0">
                  <Upload className="w-6 h-6" />
                </div>
              )}
              <div>
                <input ref={logoInput} type="file" accept="image/*" className="hidden"
                  onChange={(e) => upload("logo", e.target.files?.[0])} data-testid="vs-logo-input" />
                <Button type="button" variant="outline" className="rounded-full border-sage/40 text-forest"
                  onClick={() => logoInput.current?.click()} disabled={uploading === "logo"} data-testid="vs-logo-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading === "logo" ? "Uploading..." : (profile.logo_path ? "Replace logo" : "Upload logo")}
                </Button>
                <p className="text-xs text-forest/50 mt-1">Square JPG or PNG, up to 10MB.</p>
              </div>
            </div>
          </section>

          {/* Business details */}
          <section className="space-y-4">
            <div>
              <Label className="text-forest/80 font-bold">Business name *</Label>
              <Input value={profile.business_name}
                onChange={(e) => setProfile({ ...profile, business_name: e.target.value })}
                className="mt-1" data-testid="vs-business-name" />
            </div>
            <div>
              <Label className="text-forest/80 font-bold">Tagline</Label>
              <Input value={profile.tagline}
                onChange={(e) => setProfile({ ...profile, tagline: e.target.value })}
                className="mt-1" placeholder="One-line pitch" data-testid="vs-tagline" />
            </div>
            <div>
              <Label className="text-forest/80 font-bold">About your business</Label>
              <Textarea value={profile.description}
                onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                rows={4} className="mt-1"
                placeholder="Tell families what you offer, ages served, approach..."
                data-testid="vs-description" />
            </div>

            <div>
              <Label className="text-forest/80 font-bold">Categories</Label>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="vs-categories">
                {CATEGORIES.map((c) => (
                  <button key={c} type="button" onClick={() => toggleCategory(c)}
                    className={`text-xs font-bold px-3 py-1.5 rounded-full border transition-colors ${profile.categories.includes(c) ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                    data-testid={`vs-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-forest/80 font-bold">Location / service area</Label>
                <Input value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  className="mt-1" data-testid="vs-location" />
              </div>
              <div>
                <Label className="text-forest/80 font-bold">Phone</Label>
                <Input value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="mt-1" data-testid="vs-phone" />
              </div>
              <div>
                <Label className="text-forest/80 font-bold">Public contact email</Label>
                <Input value={profile.contact_email}
                  onChange={(e) => setProfile({ ...profile, contact_email: e.target.value })}
                  className="mt-1" data-testid="vs-contact-email" />
              </div>
              <div>
                <Label className="text-forest/80 font-bold">Website</Label>
                <Input value={profile.website}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  className="mt-1" placeholder="https://..." data-testid="vs-website" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-forest/80 font-bold">Social media</Label>
                <Input value={profile.social_media}
                  onChange={(e) => setProfile({ ...profile, social_media: e.target.value })}
                  className="mt-1" placeholder="Instagram, Facebook or TikTok handle / link"
                  data-testid="vs-social-media" />
              </div>
            </div>
          </section>

          {/* Multi-branch */}
          <section>
            <Label className="text-forest/80 font-bold">Do you have multiple branches?</Label>
            <div className="mt-2 flex gap-2" data-testid="vs-multi-branch-toggle">
              {[
                { v: false, label: "No" },
                { v: true, label: "Yes" },
              ].map((o) => (
                <button
                  key={o.label}
                  type="button"
                  onClick={() => setProfile({
                    ...profile,
                    has_multiple_branches: o.v,
                    branches: o.v ? (profile.branches.length ? profile.branches : [{ ...EMPTY_BRANCH }]) : [],
                  })}
                  className={`text-xs font-bold px-4 py-2 rounded-full border transition-colors ${profile.has_multiple_branches === o.v ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
                  data-testid={`vs-multi-branch-${o.label.toLowerCase()}`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {profile.has_multiple_branches && (
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-forest/80 font-bold">How many branches?</Label>
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={profile.branches.length || ""}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(20, parseInt(e.target.value) || 0));
                      const arr = Array.from({ length: n }, (_, i) => profile.branches[i] || { ...EMPTY_BRANCH });
                      setProfile({ ...profile, branches: arr });
                    }}
                    className="mt-1 w-32"
                    data-testid="vs-branch-count"
                  />
                </div>

                <div className="space-y-4" data-testid="vs-branches-list">
                  {profile.branches.map((b, i) => (
                    <div key={i} className="bg-softstone/60 rounded-2xl p-4 space-y-3" data-testid={`vs-branch-${i}`}>
                      <p className="text-sm font-bold text-forest">Branch {i + 1}</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label>Branch name</Label>
                          <Input
                            value={b.name}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, name: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-name-${i}`}
                          />
                        </div>
                        <div>
                          <Label>Address</Label>
                          <Input
                            value={b.address}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, address: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-address-${i}`}
                          />
                        </div>
                        <div>
                          <Label>City</Label>
                          <Input
                            value={b.city}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, city: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-city-${i}`}
                          />
                        </div>
                        <div>
                          <Label>Province</Label>
                          <Input
                            value={b.province}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, province: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-province-${i}`}
                          />
                        </div>
                        <div>
                          <Label>Phone number</Label>
                          <Input
                            value={b.phone}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, phone: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-phone-${i}`}
                          />
                        </div>
                        <div>
                          <Label>Branch email</Label>
                          <Input
                            type="email"
                            value={b.email}
                            onChange={(e) => { const arr = [...profile.branches]; arr[i] = { ...b, email: e.target.value }; setProfile({ ...profile, branches: arr }); }}
                            className="mt-1 rounded-xl bg-white border-sage/30"
                            data-testid={`vs-branch-email-${i}`}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Services */}
          <section>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-forest/80 font-bold">Services</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-full"
                onClick={addService} data-testid="vs-add-service-btn">
                <Plus className="w-4 h-4 mr-1" /> Add service
              </Button>
            </div>
            <div className="space-y-3" data-testid="vs-services-list">
              {profile.services.map((s, i) => (
                <div key={i}
                  className="bg-softstone/60 rounded-2xl p-4 grid sm:grid-cols-[1fr_1fr_140px_40px] gap-3 items-start">
                  <Input value={s.name}
                    onChange={(e) => setServiceField(i, "name", e.target.value)}
                    placeholder="Service name"
                    className="rounded-xl bg-white border-sage/30"
                    data-testid={`vs-service-name-${i}`} />
                  <Input value={s.description}
                    onChange={(e) => setServiceField(i, "description", e.target.value)}
                    placeholder="Short description"
                    className="rounded-xl bg-white border-sage/30"
                    data-testid={`vs-service-desc-${i}`} />
                  <Input value={s.price}
                    onChange={(e) => setServiceField(i, "price", e.target.value)}
                    placeholder="R price"
                    className="rounded-xl bg-white border-sage/30"
                    data-testid={`vs-service-price-${i}`} />
                  <button type="button" onClick={() => removeService(i)}
                    className="p-2 text-terracotta-dark hover:bg-terracotta-light rounded-xl"
                    data-testid={`vs-service-remove-${i}`} aria-label="Remove service">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {profile.services.length === 0 && (
                <p className="text-xs text-forest/50">No services yet — add your first one above.</p>
              )}
            </div>
          </section>

          {/* Product photos */}
          <section>
            <Label className="text-forest/80 font-bold">Product photos ({(profile.image_paths || []).length}/4)</Label>
            <p className="text-xs text-forest/50 mb-2">Up to 4 photos of your products, space, or classes.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3" data-testid="vs-images-grid">
              {(profile.image_paths || []).map((p) => (
                <div key={p} className="relative group">
                  <img src={fileUrl(p)} alt="Product" className="w-full h-28 object-cover rounded-2xl border border-sage/20" />
                  <button type="button" onClick={() => removeFile(p, "image")}
                    className="absolute top-1.5 right-1.5 bg-terracotta text-white rounded-full p-1 shadow opacity-90 hover:opacity-100"
                    aria-label="Remove photo" data-testid={`vs-image-remove-${p}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={imageInput} type="file" accept="image/*" className="hidden"
              onChange={(e) => upload("image", e.target.files?.[0])} data-testid="vs-image-input" />
            <Button type="button" variant="outline" className="rounded-full"
              onClick={() => imageInput.current?.click()}
              disabled={(profile.image_paths || []).length >= 4 || uploading === "image"}
              data-testid="vs-image-btn">
              <Upload className="w-4 h-4 mr-2" />
              {uploading === "image" ? "Uploading..." : ((profile.image_paths || []).length >= 4 ? "Photo limit reached (4/4)" : "Add photo")}
            </Button>
          </section>

          {/* Flyers (PDF) */}
          <section>
            <Label className="text-forest/80 font-bold">Flyers & brochures (PDF)</Label>
            <p className="text-xs text-forest/50 mb-2">Promotional PDFs parents can download from your page.</p>
            <div className="space-y-2 mb-3" data-testid="vs-flyers-list">
              {(profile.flyer_paths || []).map((f) => (
                <div key={f.path} className="flex items-center gap-3 bg-softstone/60 rounded-2xl p-3">
                  <FileText className="w-5 h-5 text-terracotta-dark shrink-0" />
                  <a href={fileUrl(f.path)} target="_blank" rel="noopener noreferrer"
                    className="text-sm font-semibold text-forest hover:underline truncate">
                    {f.name}
                  </a>
                  <button type="button" onClick={() => removeFile(f.path, "flyer")}
                    className="ml-auto p-1.5 text-terracotta-dark hover:bg-terracotta-light rounded-lg"
                    data-testid={`vs-flyer-remove-${f.path}`} aria-label="Remove flyer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={flyerInput} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => upload("flyer", e.target.files?.[0])} data-testid="vs-flyer-input" />
            <Button type="button" variant="outline" className="rounded-full"
              onClick={() => flyerInput.current?.click()} disabled={uploading === "flyer"}
              data-testid="vs-flyer-btn">
              <Upload className="w-4 h-4 mr-2" /> {uploading === "flyer" ? "Uploading..." : "Upload PDF"}
            </Button>
          </section>

          {/* Submit */}
          <div className="pt-2">
            <Button onClick={save} disabled={saving}
              className="rounded-full bg-terracotta hover:bg-terracotta-dark text-white w-full shadow-md hover:-translate-y-0.5 transition-transform"
              data-testid="vs-submit-btn">
              {saving ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Submitting…</>
              ) : "Submit for Approval"}
            </Button>
          </div>
        </div>

        <Dialog
          open={submitted}
          onOpenChange={(open) => { if (!open) closeModal(); }}
        >
          <DialogContent
            data-testid="vendor-submitted-dialog"
            className="rounded-3xl"
            onEscapeKeyDown={(e) => e.preventDefault()}
            onPointerDownOutside={(e) => e.preventDefault()}
            onInteractOutside={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <div className="mx-auto mb-3 inline-flex w-14 h-14 rounded-full bg-sage-light items-center justify-center">
                <Mail className="w-7 h-7 text-sage-dark" />
              </div>
              <DialogTitle className="text-center font-heading font-black text-forest text-xl" data-testid="vendor-submitted-title">
                Profile submitted for approval
              </DialogTitle>
              <DialogDescription className="text-center text-forest/70 pt-2" data-testid="vendor-submitted-msg">
                Thanks! Your profile has been submitted to our admin team for review. You'll
                receive an email once you're approved so you can complete payment and go live.
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ───────────────────────────────────────────────────────────────────────
  // DASHBOARD: approved vendors (layout unchanged).
  // ───────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="vendor-portal">
      <div className="mb-6 fade-in">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Vendor portal</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Your business hub</h1>
        <InviteTrialBadge />
      </div>

      {statusBanner.text && (
        <div className={`flex items-start gap-3 border rounded-2xl p-4 mb-6 text-sm ${statusBanner.cls}`} data-testid="vendor-status-banner">
          <statusBanner.icon className="w-5 h-5 shrink-0 mt-0.5" />
          <p>{statusBanner.text}</p>
        </div>
      )}

      <YourStatsCard />

      <section className="mb-8" data-testid="vendor-business-summary">
        <h2 className="font-heading font-bold text-forest text-lg mb-3">Your business</h2>
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm flex items-start gap-5">
          {profile.logo_path ? (
            <img src={fileUrl(profile.logo_path)} alt="Business logo" className="w-20 h-20 rounded-2xl object-cover border border-sage/30 shrink-0" data-testid="vendor-summary-logo" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-sage-light flex items-center justify-center shrink-0" data-testid="vendor-summary-logo-empty">
              <Store className="w-7 h-7 text-sage-dark" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-lg font-heading font-bold text-forest break-words" data-testid="vendor-summary-name">
              {profile.business_name || "Complete your business profile"}
            </p>
            {profile.tagline && (
              <p className="text-sm text-forest/70 italic break-words mt-0.5" data-testid="vendor-summary-tagline">{profile.tagline}</p>
            )}
            {(profile.categories || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="vendor-summary-categories">
                {profile.categories.map((c) => (
                  <span key={c} className="text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full px-2.5 py-1">{c}</span>
                ))}
              </div>
            )}
            <div className="mt-4">
              <Link to="/vendor-profile">
                <Button size="sm" variant="outline" className="rounded-full border-sage/40 text-forest hover:bg-sage-light" data-testid="vendor-edit-profile-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> {profile.business_name ? "Edit profile" : "Set up profile"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <div className="flex gap-2 mb-6 border-b border-sage/20 pb-px">
        {[["media", "Photos & Flyers"], ["inbox", `Enquiries (${requests.length})`]].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-bold rounded-t-xl transition-colors ${tab === key ? "bg-white/80 text-forest border border-b-0 border-sage/20" : "text-forest/50 hover:text-forest"}`}
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "media" && (
        <div className="space-y-6" data-testid="media-tab">
          <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading font-bold text-forest mb-1">Logo</h3>
            <p className="text-xs text-forest/50 mb-4">Shown at the top of your directory card. JPG/PNG/WebP, max 10MB.</p>
            <div className="flex items-center gap-4">
              {profile.logo_path ? (
                <div className="relative">
                  <img src={fileUrl(profile.logo_path)} alt="Logo" className="w-24 h-24 rounded-2xl object-cover border border-sage/20" data-testid="logo-preview" />
                  <button onClick={() => removeFile(profile.logo_path, "logo")} className="absolute -top-2 -right-2 bg-terracotta text-white rounded-full p-1 shadow" data-testid="logo-remove-btn" aria-label="Remove logo">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-sage-light flex items-center justify-center">
                  <Store className="w-8 h-8 text-sage-dark" />
                </div>
              )}
              <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("logo", e.target.files[0])} data-testid="logo-file-input" />
              <Button variant="outline" className="rounded-full" onClick={() => logoInput.current?.click()} data-testid="logo-upload-btn">
                <Upload className="w-4 h-4 mr-2" /> Upload logo
              </Button>
            </div>
          </div>

          <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading font-bold text-forest mb-1">Product photos ({(profile.image_paths || []).length}/4)</h3>
            <p className="text-xs text-forest/50 mb-4">Up to 4 photos of your products, space, or classes.</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="images-grid">
              {(profile.image_paths || []).map((p) => (
                <div key={p} className="relative group">
                  <img src={fileUrl(p)} alt="Upload" className="w-full h-28 object-cover rounded-2xl border border-sage/20" />
                  <button onClick={() => removeFile(p, "image")} className="absolute top-1.5 right-1.5 bg-terracotta text-white rounded-full p-1 shadow opacity-90 hover:opacity-100" data-testid={`image-remove-${p}`} aria-label="Remove photo">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={imageInput} type="file" accept="image/*" className="hidden" onChange={(e) => upload("image", e.target.files[0])} data-testid="image-file-input" />
            <Button variant="outline" className="rounded-full" onClick={() => imageInput.current?.click()} disabled={(profile.image_paths || []).length >= 4} data-testid="image-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> {(profile.image_paths || []).length >= 4 ? "Photo limit reached (4/4)" : "Add photo"}
            </Button>
          </div>

          <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm">
            <h3 className="font-heading font-bold text-forest mb-1">Flyers & brochures (PDF)</h3>
            <p className="text-xs text-forest/50 mb-4">Promotional PDFs parents can download from your page.</p>
            <div className="space-y-2 mb-4" data-testid="flyers-list">
              {(profile.flyer_paths || []).map((f) => (
                <div key={f.path} className="flex items-center gap-3 bg-softstone/60 rounded-2xl p-3">
                  <FileText className="w-5 h-5 text-terracotta-dark shrink-0" />
                  <a href={fileUrl(f.path)} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-forest hover:underline truncate">{f.name}</a>
                  <button onClick={() => removeFile(f.path, "flyer")} className="ml-auto p-1.5 text-terracotta-dark hover:bg-terracotta-light rounded-lg" data-testid={`flyer-remove-${f.path}`} aria-label="Remove flyer">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={flyerInput} type="file" accept="application/pdf" className="hidden" onChange={(e) => upload("flyer", e.target.files[0])} data-testid="flyer-file-input" />
            <Button variant="outline" className="rounded-full" onClick={() => flyerInput.current?.click()} data-testid="flyer-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload PDF
            </Button>
          </div>
        </div>
      )}

      {tab === "inbox" && (
        <div className="bg-white/80 border border-sage/20 rounded-3xl p-6 sm:p-8 shadow-sm" data-testid="inbox-tab">
          <h3 className="font-heading font-bold text-forest mb-4 flex items-center gap-2"><Inbox className="w-5 h-5 text-sage-dark" /> Enquiries from parents</h3>
          {requests.length === 0 ? (
            <p className="text-sm text-forest/50" data-testid="no-requests">No enquiries yet. Once approved, parents can reach you from your directory page.</p>
          ) : (
            <div className="space-y-3">
              {requests.map((r) => (
                <div key={r.request_id} className="bg-softstone/60 rounded-2xl p-4" data-testid={`request-${r.request_id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1 text-sm">
                    <span className="font-bold text-forest">{r.parent_name}</span>
                    <a href={`mailto:${r.parent_email}`} className="text-terracotta-dark font-semibold hover:underline">{r.parent_email}</a>
                    <span className="text-xs text-forest/40 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-forest/70 whitespace-pre-line">{r.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
